<template>
  <section class="welcome-shell">
    <div v-if="showPlatformModal" class="platform-modal-backdrop">
      <div class="platform-modal" :dir="isArabic ? 'rtl' : 'ltr'">
        <h2>{{ t('welcome.mobileModal.title') }}</h2>
        <p>{{ t('welcome.appOnlyNotice') }}</p>
        <a
          class="platform-modal-btn"
          :href="platformStoreUrl"
          target="_blank"
          rel="noopener noreferrer"
          @click="showPlatformModal = false"
        >
          <img :src="platformStoreBadge" :alt="platformStoreAlt" class="platform-modal-badge" />
        </a>
      </div>
    </div>

    <div class="bg-orb bg-orb-left"></div>
    <div class="bg-orb bg-orb-right"></div>

    <section class="hero-card">
      <div class="lang-switch" :aria-label="t('language.switchLabel')">
        <button
          type="button"
          class="lang-btn"
          :class="{ active: locale === 'ar' }"
          @click="setLocale('ar')"
        >
          {{ t('language.arabic') }}
        </button>
        <button
          type="button"
          class="lang-btn"
          :class="{ active: locale === 'en' }"
          @click="setLocale('en')"
        >
          {{ t('language.english') }}
        </button>
      </div>

      <p class="kicker">{{ t('app.kicker') }}</p>

      <div class="logo-wrap">
        <div class="promba-logo">
          <img src="@/assets/logo.png" :alt="t('app.title')" />
        </div>
      </div>

      <p class="app-notice" :dir="isArabic ? 'rtl' : 'ltr'">{{ t('welcome.appOnlyNotice') }}</p>

      <div class="store-row">
        <a
          class="store-link"
          :href="iosUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="t('welcome.store.appStoreAria')"
        >
          <img :src="appStoreBadge" :alt="t('welcome.store.appStoreAlt')" class="store-badge" />
        </a>

        <a
          class="store-link"
          :href="androidUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="t('welcome.store.googlePlayAria')"
        >
          <img :src="googlePlayBadge" :alt="t('welcome.store.googlePlayAlt')" class="store-badge" />
        </a>
      </div>

      <h1 :dir="isArabic ? 'rtl' : 'ltr'">{{ t('welcome.heading') }}</h1>
      <p class="subtitle" :dir="isArabic ? 'rtl' : 'ltr'">{{ typedText }}<span class="caret">|</span></p>

      <router-link to="/terms" class="terms-link">{{ t('welcome.termsLink') }}</router-link>

      <img class="diamond-deco" src="@/assets/honor_diamond.png" alt="" aria-hidden="true" />
    </section>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import store from '@/store'
import appStoreAr from '@/assets/App_Store_Ar.svg'
import googlePlayAr from '@/assets/Google_Play_Ar.png'
import appStoreEn from '@/assets/App_Store_En.svg'
import googlePlayEn from '@/assets/Google_Play_En.png'

const { t, locale } = useI18n()

const typedText = ref('')
const iosUrl = ref('https://promba.net')
const androidUrl = ref('https://promba.net')
const showPlatformModal = ref(false)
const detectedPlatform = ref('')
let typingInterval = null
let restartInterval = null

const isArabic = computed(() => locale.value === 'ar')
const appStoreBadge = computed(() => (isArabic.value ? appStoreAr : appStoreEn))
const googlePlayBadge = computed(() => (isArabic.value ? googlePlayAr : googlePlayEn))
const platformStoreBadge = computed(() =>
  detectedPlatform.value === 'ios' ? appStoreBadge.value : googlePlayBadge.value
)
const platformStoreUrl = computed(() =>
  detectedPlatform.value === 'ios' ? iosUrl.value : androidUrl.value
)
const platformStoreAlt = computed(() =>
  detectedPlatform.value === 'ios' ? t('welcome.store.appStoreAlt') : t('welcome.store.googlePlayAlt')
)
const fetchPlatformUrl = async (platform) => {
  const { data } = await axios.post(`${store.getters.apiBaseUrl}/GetVersion`, {
    strUserName: store.getters.strUserName,
    searchKey: 1,
    platform
  })

  const url = data && data[0] && data[0].platform_url
  return url || 'https://promba.net'
}

const loadStoreLinks = async () => {
  try {
    const [ios, android] = await Promise.all([fetchPlatformUrl('ios'), fetchPlatformUrl('android')])
    iosUrl.value = ios
    androidUrl.value = android
  } catch (error) {
    console.error('Failed to load store links', error)
  }
}

const detectMobilePlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  if (/android/i.test(userAgent)) {
    return 'android'
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios'
  }
  return ''
}

const startTyping = () => {
  if (typingInterval) {
    clearInterval(typingInterval)
  }

  const fullText = t('welcome.fullText')
  typedText.value = ''
  let i = 0

  typingInterval = setInterval(() => {
    if (i < fullText.length) {
      typedText.value += fullText[i]
      i += 1
    } else {
      clearInterval(typingInterval)
      typingInterval = null
    }
  }, 45)
}

const setLocale = (nextLocale) => {
  if (nextLocale !== 'ar' && nextLocale !== 'en') {
    return
  }
  locale.value = nextLocale
}

onMounted(() => {
  startTyping()
  restartInterval = setInterval(startTyping, 20000)
  loadStoreLinks()
  detectedPlatform.value = detectMobilePlatform()
  showPlatformModal.value = detectedPlatform.value === 'android' || detectedPlatform.value === 'ios'
})

watch(
  () => locale.value,
  (newLocale) => {
    localStorage.setItem('Promba_User_Language', newLocale)
    startTyping()
  }
)

onUnmounted(() => {
  if (typingInterval) {
    clearInterval(typingInterval)
  }
  if (restartInterval) {
    clearInterval(restartInterval)
  }
})
</script>

<style scoped>
.welcome-shell {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 24px 18px 34px;
  background: radial-gradient(circle at 10% 10%, #ffead4 0%, #fff9f2 35%, #eef5ff 100%);
}

.platform-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.platform-modal {
  width: min(460px, 100%);
  background: #ffffff;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 18px 30px rgba(15, 23, 42, 0.28);
  text-align: center;
}

.platform-modal h2 {
  margin: 0 0 8px;
  color: #0f172a;
  font-size: 1.2rem;
}

.platform-modal p {
  margin: 0 0 12px;
  color: #334155;
  line-height: 1.7;
}

.platform-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  text-decoration: none;
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(249, 115, 22, 0.03);
  border: 1px solid rgba(249, 115, 22, 0.04);
  color: #7c2d12;
  font-weight: 700;
}

.platform-modal-badge {
  height: 60px;
  width: auto;
  display: block;
}

.bg-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(2px);
  opacity: 0.45;
}

.bg-orb-left {
  width: 260px;
  height: 260px;
  top: 40px;
  left: -120px;
  background: linear-gradient(145deg, #ffb26b, #ff7b54);
}

.bg-orb-right {
  width: 280px;
  height: 280px;
  right: -120px;
  bottom: 120px;
  background: linear-gradient(145deg, #6fa8ff, #3b82f6);
}

.hero-card {
  position: relative;
  z-index: 1;
  max-width: 960px;
  margin: 16px auto 20px;
  text-align: center;
}

.lang-switch {
  display: inline-flex;
  gap: 8px;
  margin-bottom: 10px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.4);
}

.lang-btn {
  border: 0;
  background: transparent;
  color: #334155;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 700;
}

.lang-btn.active {
  background: linear-gradient(90deg, #f97316, #ef4444);
  color: #fff;
}

.kicker {
  display: inline-block;
  margin: 4px auto 10px;
  padding: 6px 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #f97316, #ef4444);
  color: #fff;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}

.logo-wrap {
  margin: 10px auto 8px;
}

.promba-logo {
  border: 3px solid #f97316;
  border-radius: 50%;
  padding: 7px;
  background-color: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
  margin: 0 auto 16px;
  width: fit-content;
}

.promba-logo img {
  height: 132px;
  width: 132px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

h1 {
  color: #0f172a;
  font-size: clamp(1.75rem, 2.6vw, 2.35rem);
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.subtitle {
  font-size: 1.02rem;
  color: #334155;
  margin: 0 auto 1.3rem;
  max-width: 700px;
  min-height: 84px;
  line-height: 1.75;
}

.app-notice {
  max-width: 760px;
  margin: 0 auto 14px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(249, 115, 22, 0.12);
  border: 1px solid rgba(249, 115, 22, 0.35);
  color: #7c2d12;
  font-weight: 700;
  line-height: 1.7;
}

.caret {
  display: inline-block;
  margin-inline-start: 3px;
  font-weight: 700;
  animation: blink 1s step-end infinite;
}

.store-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.store-link {
  display: inline-flex;
  border-radius: 10px;
  transition: transform 0.2s ease;
}

.store-link:hover {
  transform: translateY(-2px);
}

.store-badge {
  height: 52px;
  width: auto;
  display: block;
}

.terms-link {
  display: inline-block;
  color: #1e3a8a;
  text-decoration: none;
  font-weight: 700;
  margin-bottom: 8px;
}

.diamond-deco {
  display: block;
  width: min(340px, 78vw);
  margin: 8px auto 0;
  opacity: 1;
  transform: rotate(-10deg);
  pointer-events: none;
  user-select: none;
}

@keyframes blink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}

@media (max-width: 600px) {
  .hero-card {
    margin: 8px auto 18px;
  }

  .promba-logo img {
    width: 118px;
    height: 118px;
  }

  .diamond-deco {
    width: min(300px, 86vw);
    margin-top: 6px;
    opacity: 1;
  }

  .subtitle {
    min-height: 98px;
    font-size: 0.95rem;
  }

  .store-badge {
    height: 48px;
  }
}

@media (prefers-color-scheme: dark) {
  .welcome-shell {
    background: radial-gradient(circle at 12% 12%, #3a1f18 0%, #0b1220 46%, #111827 100%);
  }

  .platform-modal {
    background: #0f172a;
    border: 1px solid rgba(148, 163, 184, 0.35);
  }

  .platform-modal h2 {
    color: #f8fafc;
  }

  .platform-modal p {
    color: #cbd5e1;
  }

  .platform-modal-btn {
    background: rgba(249, 115, 22, 0.08);
    border-color: rgba(251, 146, 60, 0.1);
    color: #ffedd5;
  }

  .kicker {
    background: linear-gradient(90deg, #fb923c, #ef4444);
  }

  .lang-switch {
    background: rgba(15, 23, 42, 0.55);
    border-color: rgba(148, 163, 184, 0.45);
  }

  .lang-btn {
    color: #cbd5e1;
  }

  .promba-logo {
    background-color: #0f172a;
    border-color: #334155;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
  }

  h1 {
    color: #f8fafc;
  }

  .subtitle {
    color: #cbd5e1;
  }

  .app-notice {
    background: rgba(249, 115, 22, 0.2);
    border-color: rgba(251, 146, 60, 0.5);
    color: #ffedd5;
  }

  .terms-link {
    color: #bfdbfe;
  }

  .diamond-deco {
    filter: brightness(1.08) saturate(0.85);
  }
}
</style>
